import React from 'react';
import { Box, Card, CardContent, Typography, alpha } from '@mui/material';
import { ShowChart, TrendingUp } from '@mui/icons-material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';

const PortfolioHealth = ({ equity, peakEquity, drawdown, exposure, history }) => {
  const { t } = useTranslation();
  const chartData = history.map(item => ({
    date: new Date(item.trade_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    equity: item.equity / 1000000,
    peak: item.peak_equity / 1000000,
  }));

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        background: '#ffffff',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header - Ultra Compact */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha('#3b82f6', 0.1),
              color: '#3b82f6',
            }}
          >
            <ShowChart sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.2, mb: 0.25 }}>
              {t('risk.portfolioHealth')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', lineHeight: 1 }}>
              {t('risk.performance30d')}
            </Typography>
          </Box>
        </Box>

        {/* Metrics - Clean Grid Layout */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 3,
          mb: 1.5,
          px: 2,
        }}>
          {/* Current Equity */}
          <Box>
            <Typography 
              variant="overline" 
              sx={{ 
                fontSize: '0.625rem',
                fontWeight: 600,
                letterSpacing: 0.5,
                color: 'text.secondary',
                lineHeight: 1,
                display: 'block',
                mb: 0.5,
              }}
            >
              {t('risk.current')}
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                color: '#3b82f6', 
                fontSize: '1.125rem', 
                lineHeight: 1,
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              }}
            >
              {equity ? (equity / 1000000).toFixed(2) : '0.00'}
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary', ml: 0.5 }}>
                M
              </Typography>
            </Typography>
          </Box>

          {/* Peak Equity */}
          <Box>
            <Typography 
              variant="overline" 
              sx={{ 
                fontSize: '0.625rem',
                fontWeight: 600,
                letterSpacing: 0.5,
                color: 'text.secondary',
                lineHeight: 1,
                display: 'block',
                mb: 0.5,
              }}
            >
              {t('risk.peak')}
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                color: '#10b981', 
                fontSize: '1.125rem', 
                lineHeight: 1,
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              }}
            >
              {peakEquity ? (peakEquity / 1000000).toFixed(2) : '0.00'}
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary', ml: 0.5 }}>
                M
              </Typography>
            </Typography>
          </Box>

          {/* Drawdown */}
          <Box>
            <Typography 
              variant="overline" 
              sx={{ 
                fontSize: '0.625rem',
                fontWeight: 600,
                letterSpacing: 0.5,
                color: 'text.secondary',
                lineHeight: 1,
                display: 'block',
                mb: 0.5,
              }}
            >
              {t('risk.drawdown')}
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                color: '#ef4444', 
                fontSize: '1.125rem', 
                lineHeight: 1,
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              }}
            >
              {drawdown ? (drawdown * 100).toFixed(2) : '0.00'}
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary', ml: 0.5 }}>
                %
              </Typography>
            </Typography>
          </Box>

          {/* Exposure */}
          <Box>
            <Typography 
              variant="overline" 
              sx={{ 
                fontSize: '0.625rem',
                fontWeight: 600,
                letterSpacing: 0.5,
                color: 'text.secondary',
                lineHeight: 1,
                display: 'block',
                mb: 0.5,
              }}
            >
              {t('risk.exposure')}
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                color: '#f59e0b', 
                fontSize: '1.125rem', 
                lineHeight: 1,
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              }}
            >
              {exposure ? (exposure * 100).toFixed(1) : '0.0'}
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary', ml: 0.5 }}>
                %
              </Typography>
            </Typography>
          </Box>
        </Box>

        {/* Chart - Ultra Compact */}
        {chartData.length > 0 ? (
          <Box sx={{ height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.04)} vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#9ca3af' }} tickLine={false} axisLine={false} height={20} />
                <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} tickLine={false} axisLine={false} width={35} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.6875rem', padding: '4px 8px' }}
                  formatter={(value) => value.toFixed(2) + 'M'}
                />
                <Area type="monotone" dataKey="peak" stroke="#10b981" strokeWidth={1} fill="none" strokeDasharray="2 2" />
                <Area type="monotone" dataKey="equity" stroke="#3b82f6" strokeWidth={2} fill="url(#colorEquity)" />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box 
            sx={{ 
              height: 160, 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              bgcolor: alpha('#3b82f6', 0.02),
              border: '1px dashed',
              borderColor: alpha('#3b82f6', 0.15),
              borderRadius: 1.5,
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#3b82f6', 0.08),
                color: alpha('#3b82f6', 0.6),
              }}
            >
              <TrendingUp sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', mb: 0.25 }}>
                {t('riskManagement.portfolioHealth.noDataTitle')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
                {t('riskManagement.portfolioHealth.noData')}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PortfolioHealth;
